import { AppShell, Flex } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { icons } from '../../assets/icons/icons';
import { GlobalScope } from '../../components/global-scope/global-scope';
import { UserContext } from '../../contexts';
import { useGlobalStore } from '../../store/global-store/global-store';
import { Divider } from '../../ui-library/divider/divider';
import { Logo } from '../../ui-library/logo/logo';
import { useFlora } from '../flora/flora.context';
import { ROUTES } from '../navigation/navigation';
import { ActionButton } from './action-button';
import { SidebarItem } from './side-bar-item';
import { SidebarContext } from './side-bar.context';
import { GeneralViewNames, NavItem } from './side-bar.type';

const FloraSidebarItem: NavItem = {
  id: GeneralViewNames.Flora,
  label: 'Flora',
  icon: <img src={icons.iconFlora} alt="Flora AI" style={{ width: 24, height: 24 }} />,
  path: '/application/flora-agent',
  show: true,
};

export function SideBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const useAI = useGlobalStore((state) => state.useAI);
  const { user } = useContext(UserContext);
  const { windowViewportWidth, sidebarOpen, setSidebarOpen, navItems } = useContext(SidebarContext);
  const { setIsFloraVisible } = useFlora();

  const [activeView, setActiveView] = useState<GeneralViewNames | null>(null);

  useEffect(() => {
    if (windowViewportWidth < 992) {
      setSidebarOpen(false);
    }
  }, [windowViewportWidth, setSidebarOpen]);

  useEffect(() => {
    const activeViewRoute = ROUTES.find((route) => matchPath(`/application/${route.path}`, location.pathname));
    if (activeViewRoute?.view) {
      setActiveView(activeViewRoute.view);
    }
  }, [location.pathname]);

  const handleFloraAgentClick = () => {
    setIsFloraVisible(false);
  };

  return (
    <AppShell.Navbar
      w={{ sm: sidebarOpen ? 280 : 70, base: 70 }}
      p="md"
      style={{
        height: '100%',
        minHeight: 600,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        zIndex: 60,
      }}
    >
      <Logo
        width={36}
        height={36}
        onClick={() => (user?.has_active_subscription ? navigate(`/application/strategy`) : null)}
        showText={sidebarOpen}
      />
      <AppShell.Section>
        <GlobalScope enableTooltips={!sidebarOpen} />
      </AppShell.Section>
      <Flex direction="column" style={{ flex: 1, justifyContent: 'space-between', minHeight: 0 }}>
        <Flex direction="column" gap={16}>
          <Divider />
          <AppShell.Section grow style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {navItems.map((item) => (
              <SidebarItem key={item.id} item={item} sidebarOpen={sidebarOpen} active={activeView === item.id} />
            ))}
          </AppShell.Section>
        </Flex>
        {useAI && (
          <div style={{ flexShrink: 0, marginBottom: 56 }}>
            <SidebarItem
              item={FloraSidebarItem}
              sidebarOpen={sidebarOpen}
              active={location.pathname.startsWith('/application/flora-agent')}
              onClick={handleFloraAgentClick}
            />
          </div>
        )}
      </Flex>
      {windowViewportWidth >= 992 && <ActionButton />}
    </AppShell.Navbar>
  );
}
